import {
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
} from "firebase/auth";
import { auth } from "./firebaseWeb";
import { API_BASE_URL } from "../config";

// Serialize concurrent calls so we never overlap signOut/signIn
let inflight = null;

function waitForAuthUid(expectedUid, timeoutMs = 3000) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsubscribe();
      reject(new Error("Firebase auth timeout."));
    }, timeoutMs);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const uid = user?.uid ?? null;
      if (uid === expectedUid) {
        clearTimeout(timer);
        unsubscribe();
        resolve();
      }
    });
  });
}

/**
 * Ensures the Firebase Auth session matches the given backend user.
 * Fetches a custom token from the backend and signs in if needed.
 *
 * @param {string} appJwt  - The backend JWT (stored in localStorage)
 * @param {string|number} expectedUid - The backend userId (becomes Firebase UID)
 */
export async function ensureFirebaseLoginWeb(appJwt, expectedUid) {
  if (!expectedUid) throw new Error("expectedUid is required");

  if (inflight) return inflight;

  inflight = (async () => {
    const expected = String(expectedUid);
    const currentUid = auth.currentUser?.uid ?? null;

    if (currentUid === expected) return; // already the right user

    // Match mobile: allow Firebase session restoration before fallback token fetch.
    if (currentUid == null) {
      try {
        await waitForAuthUid(expected, 3000);
        return;
      } catch {
        // continue to fallback custom-token fetch
      }
    }

    if (currentUid && currentUid !== expected) {
      try {
        await signOut(auth);
      } catch {}
    }

    const res = await fetch(`${API_BASE_URL}/auth/firebase-token`, {
      method: "POST",
      headers: { Authorization: `Bearer ${appJwt}` },
    });

    if (!res.ok) {
      throw new Error(`Failed to get Firebase custom token (${res.status})`);
    }

    const data = await res.json();
    const token =
      data?.firebaseCustomToken ?? data?.firebaseToken ?? data?.token;
    if (!token) throw new Error("Server returned no Firebase custom token");

    await signInWithCustomToken(auth, token);
  })().finally(() => {
    inflight = null;
  });

  return inflight;
}
